import { Edit } from "@mui/icons-material";
import { useRef, useState, useEffect } from "react";
import type { ProfilePictureProps } from "../types";
import { OptimizedSvg } from "@components/common/OptimizedSvg";
import { userProfileMain } from "@tvet-mis/psda-assets";
import { CircularProgress } from "@mui/material";
import { useAttachment } from "@hooks/useAttachment";

const SUPPORTED_IMAGE_FORMATS = "image/*,.jfif,.webp,image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/webp,image/jfif";

const ProfilePicture: React.FC<ProfilePictureProps> = ({ profilePicture, isUploading, onImageChange }) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [imageError, setImageError] = useState(false);

	// Check if profile picture exists and is not empty
	const hasProfilePicture = !!(profilePicture && profilePicture.trim() !== "");

	// Fetch profile picture via attachment hook (when it's an object key, not full URL or blob)
	const isDirectUrl =
		hasProfilePicture &&
		(profilePicture.startsWith("http://") ||
			profilePicture.startsWith("https://") ||
			profilePicture.startsWith("blob:"));
	const { data: fetchedProfileUrl, isLoading: isLoadingProfile } = useAttachment(
		isDirectUrl ? null : (hasProfilePicture ? profilePicture : null),
		!!(hasProfilePicture && !isDirectUrl)
	);
	const profilePictureUrl = isDirectUrl ? profilePicture : fetchedProfileUrl;
	const showProfileImage = profilePictureUrl && !imageError;

	// Reset image error when profile picture URL or profile picture data changes
	useEffect(() => {
		setImageError(false);
	}, [profilePictureUrl, profilePicture]);

	return (
		<div
			className="relative w-48 h-48 rounded-full group cursor-pointer mb-4 flex items-center justify-center"
			onClick={() => {
				if (!isUploading) {
					fileInputRef.current?.click();
				}
			}}>
			{showProfileImage ? (
				<img
					src={profilePictureUrl}
					alt="Profile"
					className="w-48 h-48 rounded-full object-cover"
					onError={() => setImageError(true)}
				/>
			) : isLoadingProfile ? (
				<div className="w-48 h-48 rounded-full flex items-center justify-center bg-gray-100">
					<CircularProgress size={40} />
				</div>
			) : (
				<div className="w-48 h-48 rounded-full flex items-center justify-center bg-gray-100">
					<OptimizedSvg
						src={userProfileMain}
						alt="user profile"
						className="user-profile-main w-full h-full"
						cacheKey="user-profile-main"
					/>
				</div>
			)}

			<input
				type="file"
				ref={fileInputRef}
				onChange={onImageChange}
				className="hidden"
				accept={SUPPORTED_IMAGE_FORMATS}
				disabled={isUploading}
			/>

			{/* Hover overlay with pencil icon */}
			<div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 rounded-full flex items-center justify-center transition-all duration-200">
				<Edit className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
			</div>
			{isUploading && (
				<div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
					<CircularProgress size={28} color="inherit" />
				</div>
			)}
		</div>
	);
};

export default ProfilePicture;
